import axios from "../config";
import type { AxiosResponse, AxiosError } from "axios";
import { generateUserKey } from "./sessionKey";

interface RequestHeaders {
  "content-type"?: string;
  "x-auth-token"?: string;
  sessionKey?: string;
}

export const backendServices = {
  get,
  post,
  put,
  putFormData,
  postFormData,
};

const inflightGetRequests = new Map<
  string,
  Promise<AxiosResponse<unknown> | AxiosError>
>();

function getRequestKey(
  url: string,
  isAuthenticated: boolean,
  params: Record<string, string | number | boolean>
): string {
  const token = isAuthenticated ? localStorage.getItem("RwaAuthToken") : "";
  return `${url}|auth:${isAuthenticated}|token:${token}|params:${JSON.stringify(params)}`;
}

async function post<TParams, TResponse>(
  url: string,
  params: TParams
): Promise<AxiosResponse<TResponse> | AxiosError> {
  const token = localStorage.getItem("RwaAuthToken");
  const result = await generateUserKey();
  const userKey = result.userKey;
  const header: RequestHeaders = token
    ? {
        "content-type": "application/json",
        "x-auth-token": token,
        sessionKey: userKey,
      }
    : { "content-type": "application/json", sessionKey: userKey };

  try {
    const response = await axios.post<TResponse>(url, params, {
      headers: header,
    });
    return response;
  } catch (error) {
    return error as AxiosError;
  }
}

async function get<
  TResponse,
  TParams extends Record<string, string | number | boolean> = Record<
    string,
    never
  >
>(
  url: string,
  isAuthenticated: boolean,
  params: TParams = {} as TParams
): Promise<AxiosResponse<TResponse> | AxiosError> {
  const requestKey = getRequestKey(
    url,
    isAuthenticated,
    params as Record<string, string | number | boolean>
  );
  const inflightRequest = inflightGetRequests.get(requestKey);
  if (inflightRequest) {
    return inflightRequest as Promise<AxiosResponse<TResponse> | AxiosError>;
  }

  const token = localStorage.getItem("RwaAuthToken");
  const header: RequestHeaders =
    isAuthenticated && token
      ? { "x-auth-token": token, "content-type": "application/json" }
      : { "content-type": "application/json" };

  const requestPromise = (async () => {
    try {
      const response = await axios.get<TResponse>(url, {
        headers: header,
        params,
      });
      return response;
    } catch (error) {
      return error as AxiosError;
    } finally {
      inflightGetRequests.delete(requestKey);
    }
  })();

  inflightGetRequests.set(requestKey, requestPromise);
  return requestPromise;
}

async function put<TParams, TResponse>(
  url: string,
  parameters: TParams
): Promise<AxiosResponse<TResponse> | AxiosError> {
  const token = localStorage.getItem("RwaAuthToken");
  const result = await generateUserKey();
  const userKey = result.userKey;
  const header: RequestHeaders = token
    ? {
        "x-auth-token": token,
        "content-type": "application/json",
        sessionKey: userKey,
      }
    : { "content-type": "application/json" };

  try {
    const response = await axios.put<TResponse>(url, parameters, {
      headers: header,
    });
    return response;
  } catch (error) {
    return error as AxiosError;
  }
}

async function putFormData<TResponse>(
  url: string,
  formData: FormData
): Promise<AxiosResponse<TResponse> | AxiosError> {
  const token = localStorage.getItem("RwaAuthToken");
  const result = await generateUserKey();
  const userKey = result.userKey;
  const header: RequestHeaders = token
    ? { "x-auth-token": token, sessionKey: userKey }
    : { sessionKey: userKey };

  try {
    const response = await axios.put<TResponse>(url, formData, {
      headers: header,
      // Don't set content-type for FormData, let axios handle it
    });
    return response;
  } catch (error) {
    return error as AxiosError;
  }
}

async function postFormData<TResponse>(
  url: string,
  formData: FormData
): Promise<AxiosResponse<TResponse> | AxiosError> {
  const token = localStorage.getItem("RwaAuthToken");
  const result = await generateUserKey();
  const userKey = result.userKey;
  const header: RequestHeaders = token
    ? { "x-auth-token": token, sessionKey: userKey }
    : { sessionKey: userKey };

  try {
    const response = await axios.post<TResponse>(url, formData, {
      headers: header,
    });
    return response;
  } catch (error) {
    return error as AxiosError;
  }
}
